import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './lib/auth.js';
import { Layout } from './components/Layout.js';
import { ParentLayout } from './components/ParentLayout.js';
import { Login } from './screens/Login.js';
import { AcceptInvite } from './screens/AcceptInvite.js';
// Clinician screens
import { Overview } from './screens/Overview.js';
import { Labs } from './screens/Labs.js';
import { Serology } from './screens/Serology.js';
import { Medications } from './screens/Medications.js';
import { DoseReview } from './screens/DoseReview.js';
import { Appointments } from './screens/Appointments.js';
import { Adherence } from './screens/Adherence.js';
import { Observations } from './screens/Observations.js';
import { Attachments } from './screens/Attachments.js';
import { Reports } from './screens/Reports.js';
import { ReportsInbox } from './screens/ReportsInbox.js';
import { Admin } from './screens/Admin.js';
import { ClinicalNotes } from './screens/ClinicalNotes.js';
import { Settings } from './screens/Settings.js';
// Parent screens
import { ParentToday } from './screens/parent/Today.js';
import { ParentJournal } from './screens/parent/Journal.js';
import { ParentTrends } from './screens/parent/Trends.js';
import { ParentPlan } from './screens/parent/Plan.js';
import { ParentSchedule } from './screens/parent/Schedule.js';
import { ParentMore } from './screens/parent/More.js';
import { ParentHistory } from './screens/parent/History.js';

function Gate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-8 text-slate-400">…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

function ClinicianRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/labs" element={<Labs />} />
        <Route path="/serology" element={<Serology />} />
        <Route path="/medications" element={<Medications />} />
        <Route path="/dose-review" element={<DoseReview />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/adherence" element={<Adherence />} />
        <Route path="/observations" element={<Observations />} />
        <Route path="/attachments" element={<Attachments />} />
        <Route path="/reports-inbox" element={<ReportsInbox />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/notes" element={<ClinicalNotes />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function ParentRoutes() {
  return (
    <ParentLayout>
      <Routes>
        <Route path="/" element={<ParentToday />} />
        <Route path="/journal" element={<ParentJournal />} />
        <Route path="/trends" element={<ParentTrends />} />
        <Route path="/plan" element={<ParentPlan />} />
        <Route path="/schedule" element={<ParentSchedule />} />
        <Route path="/history" element={<ParentHistory />} />
        <Route path="/more" element={<ParentMore />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ParentLayout>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  return user?.role === 'caregiver' ? <ParentRoutes /> : <ClinicianRoutes />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/*" element={<Gate><AppRoutes /></Gate>} />
    </Routes>
  );
}
